"use client";

import { useQuery, QueryClientContext } from "@tanstack/react-query";
import { useState, useEffect, useContext } from "react";
import type { AuthUser } from "./types";

const CURRENT_USER_QUERY_KEY = ["current-user"] as const;

function fetchUserFromApi(): Promise<AuthUser | null> {
  return fetch("/api/auth/me")
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => (data && data.user ? (data.user as AuthUser) : null))
    .catch((err) => {
      console.error("Error fetching current user:", err);
      return null;
    });
}

function useQueryCurrentUser() {
  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: fetchUserFromApi,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useCurrentUser() {
  const clientContext = useContext(QueryClientContext);
  const [fallbackUser, setFallbackUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    if (!clientContext) {
      fetchUserFromApi().then((u) => setFallbackUser(u));
    }
  }, [clientContext]);

  if (!clientContext) {
    return {
      data: fallbackUser,
      isLoading: false,
      isError: false,
      error: null,
      refetch: async () => {
        const u = await fetchUserFromApi();
        setFallbackUser(u);
        return { data: u, isSuccess: true, isError: false } as any;
      },
    } as any;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useQueryCurrentUser();
}
